import dynamic from "next/dynamic";
import { CounterUp, BgBanner, Blockquote, Rating, Container, Pricing, Switcher, IconText, Faq, FreemiusButton, SubscribeButton, InfoBox, FilterTab, JltButtons, JltButton, NewPricing, LiveDemoForm, Listing, BundlePricing, WooCommerceButton, JltAppsumoCodeRedemption, JltFreemiusAffiliateForm, FluentFormClient, JltContainerScroll } from "./clientJltBlocks";
import { BlockRenderer } from ".";
import { Suspense } from "react";
import { buildCheckoutQuery } from "@litonarefin/utils/checkoutQuery";
import { bundlePricingAttributes } from "@litonarefin/components/JltBlocks/bundlePricingAttributes";

/**
 * Encrypts a Paddle block's checkout payload and hands the result down as
 * `checkoutQuery`.
 *
 * This runs here, in the server BlockRenderer, rather than inside the block
 * components: those get pulled into the client bundle (BundlePricing is a client
 * component and imports BundlePaddleCheckout), and the AES key is server-only.
 * See utils/hashData.js for why.
 */
const withCheckoutQuery = (attributes = {}) => ({
  ...attributes,
  checkoutQuery: buildCheckoutQuery(attributes),
});

// BundlePricing ignores the block's attributes and renders a static config, so its
// checkout queries are derived from that same config here — one per tab, in tab
// order — and passed down. Same reason as above: it is a client component.
const bundleCheckoutQueries = () =>
  (bundlePricingAttributes?.tabs || []).map((tab) => buildCheckoutQuery(tab?.button));

// //jlt blocks
const Product = dynamic(() => import("@litonarefin/components/JltBlocks/Product"));
const Support = dynamic(() => import("@litonarefin/components/JltBlocks/Support"));
const SwitcherChild = dynamic(() => import("@litonarefin/components/JltBlocks/SwitcherChild"));
const PriceCard = dynamic(() => import("@litonarefin/components/JltBlocks/PriceCard"));
const DocsCard = dynamic(() => import("@litonarefin/components/JltBlocks/DocsCard"));
const PaddleCheckout = dynamic(() => import("@litonarefin/components/JltBlocks/PaddleCheckout"));
const BundlePaddleCheckout = dynamic(() =>
  import("@litonarefin/components/JltBlocks/BundlePaddleCheckout")
);
const DocsSearchBlock = dynamic(() => import("@litonarefin/components/JltBlocks/DocsSearchBlock"));
const WooCommearceReviews = dynamic(() =>
  import("@litonarefin/components/JltBlocks/WooCommearceReviews")
);
const SubscribeForm = dynamic(() => import("@litonarefin/components/JltBlocks/SubscribeForm"));
const FeaturedPlugin = dynamic(() => import("@litonarefin/components/JltBlocks/FeaturedPlugin"));
const AdvancedPricing = dynamic(() => import("@litonarefin/components/JltBlocks/AdvancedPricing"));
const JltContactForm = dynamic(() => import("@litonarefin/components/JltBlocks/JltContactForm"));
const JltFluentForm = dynamic(() => import("@litonarefin/components/JltBlocks/FluentForm"));
const ComparisonTable = dynamic(() => import("@litonarefin/components/JltBlocks/ComparisonTable"));
const GlossaryPosts = dynamic(() => import("@litonarefin/components/JltBlocks/GlossaryPosts"));

export const jltBlocks = {
  ["jlt-core/pricing-bundle"]: (block) => (
    <BundlePricing
      key={block?.id}
      attributes={block?.attributes}
      checkoutQueries={bundleCheckoutQueries()}
    />
  ),
  ["jlt-core/advanced-pricing"]: (block) => (
    <AdvancedPricing key={block?.id} attributes={block?.attributes} />
  ),
  ["jlt-core/filter-tab"]: (block) => <FilterTab key={block?.id} attributes={block?.attributes} />,
  ["jlt-core/info-box"]: (block) => <InfoBox key={block?.id} attributes={block?.attributes} />,
  ["jlt-core/featured-plugin"]: (block) => (
    <FeaturedPlugin key={block?.id} attributes={block?.attributes} />
  ),
  ["jlt-core/subscribe-form"]: (block) => (
    <SubscribeForm key={block?.id} attributes={block?.attributes} />
  ),
  ["jlt-core/product"]: (block) => <Product key={block?.id} attributes={block?.attributes} />,
  ["jlt-core/support"]: (block) => <Support key={block?.id} attributes={block?.attributes} />,
  ["jlt-core/bg-banner"]: (block) => (
    <BgBanner key={block?.id} attributes={block?.attributes}>
      <BlockRenderer blocks={block?.innerBlocks} />
    </BgBanner>
  ),
  ["jlt-core/blockquote"]: (block) => <Blockquote key={block?.id} attributes={block?.attributes} />,
  ["jlt-core/rating"]: (block) => <Rating key={block?.id} attributes={block?.attributes} />,
  ["jlt-core/container"]: (block) => (
    <Suspense fallback="" key={block?.id}>
      <Container attributes={block?.attributes}>
        <BlockRenderer blocks={block?.innerBlocks} />
      </Container>
    </Suspense>
  ),
  ["jlt-core/pricing"]: (block) => (
    <Pricing key={block?.id} attributes={block?.attributes}>
      <BlockRenderer blocks={block?.innerBlocks} />
    </Pricing>
  ),
  ["jlt-core/switcher"]: (block) => (
    <Switcher key={block?.id} attributes={block?.attributes} blocks={block?.innerBlocks} />
  ),
  ["jlt-core/switcher-child"]: (block) => (
    <SwitcherChild key={block?.id}>
      <BlockRenderer blocks={block?.innerBlocks} />
    </SwitcherChild>
  ),
  ["jlt-core/icon-text"]: (block) => <IconText key={block?.id} attributes={block?.attributes} />,
  ["jlt-core/price-card"]: (block) => <PriceCard key={block?.id} attributes={block?.attributes} />,
  ["jlt-core/docs-card"]: (block) => <DocsCard key={block?.id} attributes={block?.attributes} />,
  ["jlt-core/faq"]: (block) => <Faq key={block?.id} attributes={block?.attributes} />,
  ["jlt-core/freemius-button"]: (block) => (
    <FreemiusButton key={block?.id} attributes={block?.attributes} />
  ),
  ["jlt-core/woo-commerce-button"]: (block) => (
    <WooCommerceButton key={block?.id} attributes={block?.attributes} />
  ),
  ["jlt-core/paddle-checkout"]: (block) => (
    <PaddleCheckout key={block?.id} attributes={withCheckoutQuery(block?.attributes)} />
  ),
  ["jlt-core/bundle-paddle-checkout"]: (block) => (
    <BundlePaddleCheckout key={block?.id} attributes={withCheckoutQuery(block?.attributes)} />
  ),
  ["jlt-core/subscribe-button"]: (block) => (
    <SubscribeButton key={block?.id} attributes={block?.attributes} />
  ),
  // // ["jlt-core/blog-category"]: (block) =>    return <JltBlogCategory key={block?.id} attributes={block?,attributes} />;// // }
  ["jlt-core/docs-search"]: (block) => <DocsSearchBlock key={block?.id} />,
  ["jlt-core/woo-commerce-review"]: (block) => (
    <WooCommearceReviews key={block.id} attributes={block?.attributes} />
  ),
  ["jlt-core/counter-up"]: (block) => <CounterUp key={block?.id} attributes={block?.attributes} />,
  //
  ["jlt-core/buttons"]: (block) => (
    <JltButtons key={block.id} attributes={block?.attributes}>
      <BlockRenderer blocks={block.innerBlocks} />
    </JltButtons>
  ),
  ["jlt-core/button"]: (block) => <JltButton key={block?.id} attributes={block?.attributes} />,
  ["jlt-core/contact-form"]: (block) => <JltContactForm key={block?.id} />,
  ["jlt-core/appsumo-code-redemption"]: (block) => <JltAppsumoCodeRedemption key={block?.id} />,
  ["jlt-core/freemius-affiliate-form"]: (block) => <JltFreemiusAffiliateForm key={block?.id} />,
  ["jlt-core/fluent-form"]: (block) => {
    if (block?.attributes?.isLagyLoad) {
      return <FluentFormClient key={block?.id} attributes={block?.attributes} />;
    } else {
      return <JltFluentForm key={block?.id} attributes={block?.attributes} />;
    }
  },
  ["jlt-core/comparison-table"]: (block) => (
    <ComparisonTable key={block?.id} attributes={block?.attributes} />
  ),
  ["jlt-core/glossary-posts"]: (block) => <GlossaryPosts key={block?.id} />,
  ["jlt-core/container-scroll"]: (block) => (
    <JltContainerScroll key={block?.id} attributes={block?.attributes} />
  ),
  ["jlt-core/new-pricing"]: (block) => (
    <NewPricing key={block?.id} attributes={block?.attributes} />
  ),
  ["jlt-core/live-demo-form"]: (block) => (
    <LiveDemoForm key={block?.id} attributes={block?.attributes} />
  ),
  ["jlt-core/listing"]: (block) => <Listing key={block?.id} attributes={block?.attributes} />,
};
